import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../../hooks/storeHooks';
import {
  resetApiCompletedWells,
  resetFailedAssets,
  resetNodeIdStatusMap,
  resetProcessingStartTimes,
  resetScannedWellsData,
  resetScanProgressCount,
  resetTimedOutWells,
  selectProgressDisplay,
  selectScanStats,
  setIsTableOpen,
  setIsTableScanning,
  setProcessingStartTimes,
  setTimedOutWells,
} from './WellControlSlice';
import { ReactComponent as InfoIcon } from '../../../images/info-circle.svg';
import { ReactComponent as CloseIcon } from '../../../images/x-close.svg';
import './WellScanMonitor.scss';
import { ALLY_CONFIGURATIONS } from '../../../services/configuration/config';
import { useNavigate } from 'react-router-dom';
import { WELL_CONTROL_ACTION_LABELS } from '../MultiwellControlActions/wellControlActionLabels';

const WellScanMonitor: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const scanState = useAppSelector((state) => state.wellControl);
  const scanStats = useAppSelector(selectScanStats);
  const { failedCount, processedCount, totalCount } = scanStats;

  const progressDisplay = useAppSelector(selectProgressDisplay);
  const PROCESSING_TIMEOUT_MS = ALLY_CONFIGURATIONS.WELL_SCAN_TIMEOUT_MS;

  const { apiCompletedWells, scannedWellsData, actionCommand = 'Scan', processingStartTimes } = scanState;

  const actionLabels = WELL_CONTROL_ACTION_LABELS[actionCommand] || WELL_CONTROL_ACTION_LABELS.Scan;
  const displayButtonLabel = actionCommand === 'Scan' || !actionCommand ? 'Well scan' : actionLabels.button;

  const [visible, setVisible] = useState(true);

  const handleViewChanges = () => {
    const groupName = scanState.selectedGroupName || '';
    const customViewsPath = `/group/customViews/${groupName}`;
    if (location.pathname === customViewsPath) {
      dispatch(setIsTableOpen(true));
    } else {
      navigate(customViewsPath);
      setTimeout(() => {
        dispatch(setIsTableOpen(true));
      }, 100);
    }
  };

  useEffect(() => {
    if (scanState.isTableScanning && scannedWellsData.length > 0) {
      const now = Date.now();
      const newTimes: Record<string, number> = {};
      scannedWellsData.forEach((well) => {
        newTimes[well.assetId] = now;
      });
      dispatch(setProcessingStartTimes(newTimes));
    }
  }, [scanState.isTableScanning, scannedWellsData, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const newTimedOut: Record<string, boolean> = {};

      scannedWellsData.forEach((well) => {
        const wellId = well.assetId;
        const startTime = processingStartTimes[wellId];
        const completionStatus = apiCompletedWells[wellId];
        const timeElapsed = startTime ? now - startTime : 0;

        if (completionStatus === null && startTime && timeElapsed > PROCESSING_TIMEOUT_MS) {
          newTimedOut[wellId] = true;
        }
      });

      if (Object.keys(newTimedOut).length > 0) {
        dispatch(setTimedOutWells(newTimedOut));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [
    processingStartTimes,
    apiCompletedWells,
    scannedWellsData,
    dispatch,
    PROCESSING_TIMEOUT_MS,
    scanState.isTableScanning,
  ]);

  useEffect(() => {
    if (scanState.isTableScanning) {
      setVisible(true);
    }
  }, [scanState.isTableScanning]);

  const handleMonitorClose = () => {
    setVisible(false);
    dispatch(setIsTableOpen(false));
    dispatch(setIsTableScanning(false));
    dispatch(resetScanProgressCount());
    dispatch(resetApiCompletedWells());
    dispatch(resetNodeIdStatusMap());
    dispatch(resetScannedWellsData());
    dispatch(resetFailedAssets());
    dispatch(resetTimedOutWells());
    dispatch(resetProcessingStartTimes());
  };

  if (!scanState.isTableScanning) return null;

  if (!visible || scanState.isTableOpen) {
    return <div style={{ display: 'none' }} />;
  }

  return (
    <div className='fixed bottom-5 right-4 well-scan-monitor-custom-views'>
      <div className='well-scan-monitor-row' style={{ gap: processedCount < totalCount ? '' : '18px' }}>
        <div className='well-scan-signal-col'>
          {processedCount < totalCount ? (
            <></>
          ) : failedCount > 0 ? (
            <span className='well-scan-error-icon'>
              <InfoIcon />
            </span>
          ) : (
            <span className='well-scan-success-icon'>
              <InfoIcon />
            </span>
          )}
        </div>
        <div className='well-scan-content-col'>
          {processedCount < totalCount ? (
            <div className='well-scan-header'>
              <span className='well-scan-header-text'>{displayButtonLabel} in progress</span>
              <span className='well-scan-description'>This might take a minute. We will alert you when complete.</span>
            </div>
          ) : (
            <div className='well-scan-header'>
              <span className='well-scan-header-text'>{displayButtonLabel} successful</span>
              <span className='well-scan-description'>
                {failedCount > 0
                  ? `${actionLabels.progressive} complete. Some wells failed`
                  : `Great news, your wells ${actionLabels.successMessage.toLowerCase()} successfully. No issues detected.`}
                {failedCount > 0 && (
                  <>
                    <br />
                    {'Would you like to view the wells that failed?'}
                  </>
                )}
              </span>
            </div>
          )}
          {processedCount < totalCount && (
            <>
              <div className='w-full bg-gray-700 rounded h-2 mb-2'>
                <div className='bg-blue-400 h-2 rounded' style={{ width: `${progressDisplay}%` }} />
              </div>
              <div className='well-scan-count-right'>
                {processedCount} of {totalCount} scanned...
              </div>
            </>
          )}

          <div className='well-scan-actions-row'>
            <button className='well-scan-view-changes' type='button' onClick={handleViewChanges}>
              {failedCount > 0 ? `View failed` : `View progress`}
            </button>
          </div>
          {processedCount >= totalCount && (
            <button className='well-scan-close-icon' type='button' onClick={handleMonitorClose} aria-label='Close'>
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WellScanMonitor;
